import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../App';
import { refreshTokenApi } from '../api/auth';

// 单例 WebSocket，避免多处组件重复建立连接（开发模式 StrictMode 会双渲染）
let sharedSocket: WebSocket | null = null;
let sharedSocketToken: string | null = null;
let sharedIsConnected = false;
let sharedReconnectTimeout: NodeJS.Timeout | undefined;
let sharedReconnectDelayMs = 1000; // 指数退避起始 1s，最大 30s
let sharedMessageHandler: ((event: MessageEvent) => void) | null = null;
let sharedHeartbeatTimer: NodeJS.Timeout | undefined;
let sharedIsConnecting = false; // 防止并发连接的锁

interface WebSocketMessage {
  type: string;
  payload: any;
}

export const useWebSocket = () => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated, token, refreshToken, login, logout } = useAuth();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    tokenRef.current = token || null;
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      // 用户登出，彻底清理所有 WebSocket 资源
      if (sharedSocket) {
        try { sharedSocket.close(1000, 'logout'); } catch { }
      }
      if (sharedHeartbeatTimer) {
        try { clearInterval(sharedHeartbeatTimer); } catch { }
        sharedHeartbeatTimer = undefined;
      }
      if (sharedReconnectTimeout) {
        try { clearTimeout(sharedReconnectTimeout); } catch { }
        sharedReconnectTimeout = undefined;
      }
      sharedSocket = null;
      sharedSocketToken = null;
      sharedIsConnected = false;
      sharedIsConnecting = false; // 重置连接锁
      sharedMessageHandler = null;
      sharedReconnectDelayMs = 1000;
      setSocket(null);
      setIsConnected(false);
      // console.log('[WebSocket] Cleaned up due to logout/unauthenticated');
      return;
    }

    const connect = () => {
      const currentToken = tokenRef.current;
      if (!currentToken) return;

      // 防止并发连接
      if (sharedIsConnecting) {
        // console.log('[WebSocket] Connection already in progress, skipping');
        return;
      }

      // 生产环境WebSocket URL构建逻辑
      let wsBase: string;
      if (typeof window !== 'undefined') {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsBase = `${protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;
      } else {
        // 备用方案
        const envBase = process.env.REACT_APP_API_BASE_URL as string | undefined;
        const base = envBase || '';
        wsBase = base.startsWith('ws') ? base : base.replace(/^https/, 'wss').replace(/^http/, 'ws');
      }
      // 如果已有连接但 token 变化，强制关闭并重建
      if (sharedSocket && sharedSocketToken && sharedSocketToken !== currentToken) {
        try { sharedSocket.close(1000, 'token changed'); } catch { }
        sharedSocket = null;
        sharedIsConnected = false;
      }

      const wsUrl = `${wsBase}/ws?token=${encodeURIComponent(currentToken)}`;
      // 复用单例连接，避免重复创建
      if (sharedSocket && (sharedSocket.readyState === WebSocket.OPEN || sharedSocket.readyState === WebSocket.CONNECTING)) {
        setSocket(sharedSocket);
        setIsConnected(sharedIsConnected);
        return;
      }

      sharedIsConnecting = true; // 设置连接锁
      const ws = new WebSocket(wsUrl);
      // 备选：在某些代理/环境下优先使用 Authorization 头，但浏览器原生 WebSocket 不支持自定义头；
      // 因此仍采用查询参数；服务端已同时支持从 Cookie 读取，前端可在登录后设置 cookie 以增强兼容：
      try { document.cookie = `jwt_token=${currentToken}; path=/; SameSite=Lax`; } catch { }
      sharedSocket = ws;
      sharedSocketToken = currentToken || null;

      ws.onopen = () => {
        const digest = (currentToken || '').slice(0, 8);
        // console.log('[WebSocket] Connected - Token:', digest);
        sharedIsConnected = true;
        sharedIsConnecting = false; // 连接成功，释放锁
        setIsConnected(true);
        sharedReconnectDelayMs = 1000; // 连接成功，重置退避延迟
        try { window.dispatchEvent(new CustomEvent('ws_connected')); } catch { }
        // 心跳保活：每 25 秒发送一次 ping，避免服务器超时或中间网络设备关闭空闲连接
        if (sharedHeartbeatTimer) {
          try { clearInterval(sharedHeartbeatTimer); } catch { }
        }
        sharedHeartbeatTimer = setInterval(() => {
          try {
            if (sharedSocket && sharedSocket.readyState === WebSocket.OPEN) {
              sharedSocket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
              // console.log('[WebSocket] Ping sent');
            }
          } catch (error) {
            console.error('[WebSocket] Ping failed:', error);
          }
        }, 25000);
      };

      ws.onclose = (ev) => {
        // console.log('[WebSocket] Disconnected - Code:', ev.code, 'Reason:', ev.reason);
        sharedIsConnected = false;
        sharedIsConnecting = false; // 连接关闭，释放锁
        setIsConnected(false);
        try { window.dispatchEvent(new CustomEvent('ws_disconnected', { detail: { code: ev.code, reason: ev.reason } })); } catch { }
        if (sharedHeartbeatTimer) {
          try { clearInterval(sharedHeartbeatTimer); } catch { }
          sharedHeartbeatTimer = undefined;
        }
        if (ev.code !== 1000) {
          (async () => {
            if (refreshToken) {
              try {
                // console.log('[WebSocket] Attempting token refresh...');
                const { accessToken, refreshToken: newRT } = await refreshTokenApi(refreshToken);
                login(accessToken, newRT);
                // 刷新成功后，重置退避延迟
                sharedReconnectDelayMs = 1000;
                // console.log('[WebSocket] Token refresh successful');
              } catch (error) {
                console.error('[WebSocket] Token refresh failed:', error);
                logout();
              }
            } else {
              // console.warn('[WebSocket] No refresh token available');
            }
          })();
        }
        if (sharedReconnectTimeout) clearTimeout(sharedReconnectTimeout);
        if (ev.code === 1000 && ev.reason === 'token changed') {
          sharedReconnectDelayMs = 1000;
          sharedReconnectTimeout = setTimeout(connect, 0);
        } else if (ev.code === 1000 && ev.reason === 'logout') {
          // 用户主动登出，不再自动重连
          sharedReconnectDelayMs = 1000;
          sharedReconnectTimeout = undefined;
          // console.log('[WebSocket] User logout - not reconnecting');
        } else {
          const delay = sharedReconnectDelayMs;
          // console.log(`[WebSocket] Reconnecting in ${delay}ms...`);
          sharedReconnectTimeout = setTimeout(() => {
            // console.log('[WebSocket] Attempting to reconnect...');
            connect();
          }, delay);
          sharedReconnectDelayMs = Math.min(sharedReconnectDelayMs * 2, 30000);
        }
        reconnectTimeoutRef.current = sharedReconnectTimeout;
      };

      ws.onerror = async (error) => {
        console.error('[WebSocket] Error:', error);
        try {
          ws.close(1000, 'error');
        } catch (closeError) {
          console.error('[WebSocket] Error while closing after error:', closeError);
        }
      };

      if (!sharedMessageHandler) {
        sharedMessageHandler = (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            const payload = (data && (data.payload || data.data)) || null;
            // 服务器 ping 时立即回应 pong 以保持连接活跃
            if (data?.type === 'ping') {
              try {
                if (sharedSocket && sharedSocket.readyState === WebSocket.OPEN) {
                  sharedSocket.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
                }
              } catch { }
              return;
            }
            if (data?.type === 'new_message' && payload) {
              // console.log('[WebSocket] New message received:', payload);
              window.dispatchEvent(new CustomEvent('new_message', { detail: payload }));
            }
            if (data?.type === 'message_status_update' && payload) {
              const normalized = { ...payload, status: String(payload.status || 'sent').toLowerCase() };
              // console.log('[WebSocket] Message status update:', normalized);
              window.dispatchEvent(new CustomEvent('message_status_update', { detail: normalized }));
            }
            if (data?.type === 'user_tags_updated' && payload) {
              // console.log('[WebSocket] User tags updated:', payload);
              window.dispatchEvent(new CustomEvent('user_tags_updated', { detail: payload }));
            }
          } catch (e) {
            console.error('[WebSocket] Error parsing message:', e);
          }
        };
      }
      ws.addEventListener('message', sharedMessageHandler);

      setSocket(ws);
    };

    connect();

    return () => {
      // 不主动关闭 sharedSocket，保持单例连接供其他组件复用
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, token, login, logout, refreshToken]);

  const sendMessage = (message: WebSocketMessage) => {
    if (socket && isConnected) {
      socket.send(JSON.stringify(message));
    } else {
      // console.warn('WebSocket is not connected');
    }
  };

  return { socket, isConnected, sendMessage };
};
